import React, { useState, useEffect } from "react";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import styles from "./PhotoGallery.module.css";
import { getImageUrl } from "../../utils/imageUtils";

// Dynamically fetch room data (simulate API call)
import api from "../../api/axois";

const STATIC_FALLBACK = [
  { id: 101, src: "/images/room1a.jpg", title: "1 Bedroom Apartment", category: "Rooms", description: "Unwind in this inviting lounge area, featuring plush seating, modern décor, and direct access to a comfortable bedroom." },
  { id: 102, src: "/images/room2c.jpg", title: "Modern Bathroom", category: "Rooms", description: "Experience comfort and elegance in our modern bathroom, featuring a spacious walk-in shower and sleek fixtures." },
  { id: 103, src: "/images/room3a.jpg", title: "Elegant Living & Dining Area", category: "Rooms", description: "Relax and entertain in style in our spacious living and dining area, featuring modern furnishings and a vibrant atmosphere." },
  { id: 2, src: "/images/slide2.jpg", title: "Reception Area", category: "Amenities", description: "Exclusive space for distinguished guests" },
  { id: 1001, src: "/images/service3.jpg", title: "Fitness Center", category: "Amenities", description: "State-of-the-art equipment and training" },
  { id: 1003, src: "/images/service4.jpg", title: "Concierge Services", category: "Amenities", description: "Personalized assistance around the clock" },
  { id: 1008, src: "/images/service1.jpg", title: "Private Dining", category: "Amenities", description: "Intimate spaces for special occasions" }
];

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const PhotoGallery = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [categories, setCategories] = useState(["All"]);

  // Fetch gallery images from the admin-managed API
  useEffect(() => {
    setLoading(true);
    api.get("/gallery")
      .then(res => {
        const apiImages = res.data.images || [];
        if (apiImages.length === 0) {
          // No images uploaded yet — fall back to static content
          setGalleryImages(STATIC_FALLBACK);
          setCategories(["All", "Rooms", "Amenities"]);
        } else {
          const mapped = apiImages.map(img => ({
            id: img.id,
            src: img.url,
            title: img.caption || capitalize(img.category),
            category: capitalize(img.category),
            description: img.caption || "",
          }));
          setGalleryImages(mapped);
          // Build unique category list from actual data
          const unique = ["All", ...new Set(mapped.map(i => i.category))];
          setCategories(unique);
        }
      })
      .catch(() => {
        // API unavailable — fall back to static content
        setGalleryImages(STATIC_FALLBACK);
        setCategories(["All", "Rooms", "Amenities"]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!galleryImages.length) {
      setFilteredImages([]);
      return;
    }
    if (selectedCategory === "All") {
      setFilteredImages(galleryImages);
    } else {
      setFilteredImages(galleryImages.filter(img => img.category === selectedCategory));
    }
  }, [selectedCategory, galleryImages]);

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setSelectedImage(filteredImages[prevIndex]);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading our exquisite gallery...</p>
      </div>
    );
  }

  return (
    <div className={styles.galleryContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Aurelia Gallery</h1>
            <p className={styles.heroSubtitle}>
              Discover the elegance and sophistication that defines our luxury hospitality experience
            </p>
          </div>
        </div>
        <div className={styles.heroImage}>
          <img src={getImageUrl("/images/slide1.jpg")} alt="Aurelia Hotel" />
        </div>
      </section>

      {/* Featured Carousel */}
      <section className={styles.featuredSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Featured Highlights</h2>
          <p className={styles.sectionDescription}>Experience the finest moments at Aurelia</p>
        </div>
        
        <div className={styles.carouselWrapper}>
          <Splide
            options={{
              type: 'loop',
              perPage: 3,
              perMove: 1,
              gap: '2rem',
              autoplay: true,
              interval: 5000,
              arrows: true,
              pagination: false,
              drag: true,
              wheel: true,
              wheelSleep: 300,
              breakpoints: {
                1200: { perPage: 2 },
                768: { perPage: 1, gap: '1rem' }
              }
            }}
            className={styles.splideContainer}
          >
            {(galleryImages.slice(0, 3)).map((image) => (
              <SplideSlide key={image.id}>
                <div className={styles.featuredCard} onClick={() => openLightbox(image)}>
                  <div className={styles.featuredImageWrapper}>
                    <img src={getImageUrl(image.src)} alt={image.title} />
                    <div className={styles.featuredOverlay}>
                      <div className={styles.featuredCategory}>{image.category}</div>
                      <h3 className={styles.featuredTitle}>{image.title}</h3>
                      <p className={styles.featuredDescription}>{image.description}</p>
                    </div>
                  </div>
                </div>
              </SplideSlide>
            ))}
          </Splide>
        </div>
      </section>

      {/* Category Filter */}
      <section className={styles.filterSection}>
        <div className={styles.categoryFilters}>
          {categories.map((category) => (
            <button
              key={category}
              className={`${styles.filterButton} ${selectedCategory === category ? styles.active : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery Grid */}
      <section className={styles.gallerySection}>
        <div className={styles.galleryGrid}>
          {filteredImages.map((image, index) => (
            <div 
              key={image.id}
              className={`${styles.galleryItem} ${index % 7 === 0 ? styles.large : ''}`}
              onClick={() => openLightbox(image)}
            >
              <div className={styles.imageWrapper}>
                <img src={getImageUrl(image.src)} alt={image.title} />
                <div className={styles.imageOverlay}>
                  <div className={styles.imageInfo}>
                    <h3 className={styles.imageTitle}>{image.title}</h3>
                    {image.description && (
                      <p className={styles.imageDescription}>{image.description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className={styles.lightboxOverlay} onClick={closeLightbox}>
          <div className={styles.lightboxContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeLightbox}>×</button>
            <button className={styles.navButton} onClick={prevImage}>‹</button>
            <button className={styles.navButton} onClick={nextImage}>›</button>
            
            <div className={styles.lightboxImageWrapper}>
              <img src={getImageUrl(selectedImage.src)} alt={selectedImage.title} />
            </div>
            
            <div className={styles.lightboxInfo}>
              <div className={styles.lightboxCategory}>{selectedImage.category}</div>
              <h3 className={styles.lightboxTitle}>{selectedImage.title}</h3>
              <p className={styles.lightboxDescription}>{selectedImage.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Testimonials Section */}
      <section className={styles.testimonialsSection}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Guest Experiences</h2>
          <p className={styles.sectionDescription}>What our distinguished guests say about Aurelia</p>
        </div>
        
        <div className={styles.testimonialsGrid}>
          <blockquote className={styles.testimonial}>
            <p className={styles.testimonialText}>
              "Every detail at Aurelia exudes elegance. The visual journey through their spaces is absolutely breathtaking."
            </p>
            <footer className={styles.testimonialAuthor}>- Sophia M., Dubai</footer>
          </blockquote>
          
          <blockquote className={styles.testimonial}>
            <p className={styles.testimonialText}>
              "The photography doesn't do justice to the actual beauty. Aurelia is pure architectural poetry."
            </p>
            <footer className={styles.testimonialAuthor}>- James R., London</footer>
          </blockquote>
          
          <blockquote className={styles.testimonial}>
            <p className={styles.testimonialText}>
              "From the moment you see these spaces, you understand what luxury hospitality truly means."
            </p>
            <footer className={styles.testimonialAuthor}>- Amira K., Lagos</footer>
          </blockquote>
        </div>
      </section>
    </div>
  );
};

export default PhotoGallery;